// SpinWheel.js
import React, { useState, useRef, useEffect } from "react";
import "./Spinwin.css";

const prizes = [10, 15, 20, 25, 30, 35, 40];

const SpinWheel = () => {
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [wallet, setWallet] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [coins, setCoins] = useState([]);
  const canvasRef = useRef(null);
  const audioRef = useRef(null);

  // Generate floating coins animation
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setCoins(prev => [...prev, {
          id: Date.now(),
          x: Math.random() * 100,
          y: -10,
          delay: Math.random() * 2
        }]);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Remove coins after animation
  useEffect(() => {
    if (coins.length > 20) {
      setCoins(prev => prev.slice(-20));
    }
  }, [coins]);

  // Draw wheel on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(canvas.width, canvas.height) / 2 - 10;
    const numSegments = prizes.length;
    const angleStep = (2 * Math.PI) / numSegments;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Colors for segments
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', 
      '#98D8C8', '#F7DC6F', '#BB8FCE', '#F1948A'
    ];

    // Draw segments
    for (let i = 0; i < numSegments; i++) {
      const startAngle = i * angleStep;
      const endAngle = startAngle + angleStep;
      
      // Draw segment
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      
      // Fill with gradient
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, colors[i % colors.length]);
      gradient.addColorStop(1, darkenColor(colors[i % colors.length], 20));
      ctx.fillStyle = gradient;
      ctx.fill();
      
      // Stroke
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw text
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(startAngle + angleStep / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 16px Arial';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 10;
      
      // Draw prize value with ₹ symbol
      const text = `₹${prizes[i]}`;
      ctx.fillText(text, radius * 0.65, 0);
      ctx.restore();
    }

    // Draw center circle
    const centerGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 30);
    centerGradient.addColorStop(0, '#FFD700');
    centerGradient.addColorStop(1, '#FF8C00');
    ctx.beginPath();
    ctx.arc(centerX, centerY, 30, 0, 2 * Math.PI);
    ctx.fillStyle = centerGradient;
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw "SPIN" text in center
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 5;
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SPIN', centerX, centerY);

  }, [prizes]);

  const darkenColor = (color, amount) => {
    let r = parseInt(color.slice(1,3), 16);
    let g = parseInt(color.slice(3,5), 16);
    let b = parseInt(color.slice(5,7), 16);
    r = Math.max(0, r - amount);
    g = Math.max(0, g - amount);
    b = Math.max(0, b - amount);
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
  };

  const spin = () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    setShowResult(false);

    const spins = 5 + Math.random() * 4;
    const extraAngle = Math.random() * 360;
    const totalRotation = spins * 360 + extraAngle;
    const newRotation = rotation + totalRotation;
    setRotation(newRotation);

    const prizeIndex = getPrizeIndex(newRotation);
    const prize = prizes[prizeIndex];

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }

    setTimeout(() => {
      setResult(prize);
      setShowResult(true);
      setSpinning(false);
    }, 6000);
  };

  const getPrizeIndex = (angle) => {
    const deg = angle % 360;
    const normalized = (360 - deg + 22.5) % 360;
    return Math.floor(normalized / 45);
  };

  const handleClaim = () => {
    if (result !== null) {
      setWallet(prev => prev + result);
      setShowResult(false);
      setResult(null);
    }
  };

  return (
    <div className="spin-container">
      {/* Gold Coin Background Animation */}
      <div className="coin-background">
        {coins.map(coin => (
          <div
            key={coin.id}
            className="floating-coin"
            style={{
              left: `${coin.x}%`,
              animationDelay: `${coin.delay}s`,
              '--duration': `${3 + Math.random() * 2}s`
            }}
          >
            <span className="coin-emoji">🪙</span>
          </div>
        ))}
      </div>

      {/* Glowing background effects */}
      <div className="glow-effect"></div>
      <div className="glow-effect-2"></div>

      <div className="wheel-wrapper">
        <div className="header">
          <div className="wallet-display">
            <span className="wallet-icon">👛</span>
            <span className="wallet-amount">₹{wallet}</span>
          </div>
          <h1 className="title">🎰 SPIN & WIN 🎰</h1>
        </div>

        <div className="wheel-and-pointer">
          <div className="pointer">▼</div>
          <div className="wheel-outer">
            <canvas
              ref={canvasRef}
              width="400"
              height="400"
              className="wheel"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          </div>
        </div>

        <button className="spin-btn" onClick={spin} disabled={spinning}>
          {spinning ? (
            <>
              <span className="spinner"></span>
              SPINNING...
            </>
          ) : (
            '🎯 SPIN NOW!'
          )}
        </button>

        {showResult && result !== null && (
          <div className={`result-popup show`}>
            <div className="result-content">
              <div className="confetti-container">
                <span className="confetti">🎊</span>
                <span className="confetti">🎉</span>
                <span className="confetti">✨</span>
              </div>
              <div className="result-main">
                <h2 className="congrats">🎊 CONGRATULATIONS! 🎊</h2>
                <div className="result-amount-wrapper">
                  <span className="result-amount">₹{result}</span>
                  <span className="result-coin">🪙</span>
                </div>
                <p className="result-message">You won ₹{result}! Amazing luck!</p>
                <button className="claim-btn" onClick={handleClaim}>
                  💰 Claim & Add to Wallet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <audio ref={audioRef} src="/spin-sound.mp3" preload="auto" />
    </div>
  );
};

export default SpinWheel;